import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MemeCard from "@/components/feature/MemeCard";
import { SITE_URL, useSeo } from "@/hooks/useSeo";
import { memeItems } from "@/mocks/memes";

export default function MemeDetail() {
  const { id = "" } = useParams();
  const [copied, setCopied] = useState(false);
  const item = memeItems.find((meme) => meme.id === id);

  useSeo({
    title: item ? `${item.title} - ${item.categoryName}素材 | 131吃瓜` : "内容不存在 - 131吃瓜",
    description: item
      ? `${item.summary} 收录于131吃瓜${item.categoryName}分类，可在线查看并保存使用。`
      : "该内容可能已被移动或删除，返回131吃瓜首页继续浏览最新梗图与表情包。",
    keywords: item ? `${item.title},${item.tags.join(",")},131吃瓜,梗图,表情包` : "131吃瓜,梗图,表情包",
    path: `/meme/${id}`,
    type: "article",
    image: item?.cover,
    jsonLd: item
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `${SITE_URL}/meme/${item.id}#article`,
              headline: item.title,
              description: item.summary,
              image: item.cover,
              datePublished: item.date,
              inLanguage: "zh-CN",
              author: { "@type": "Organization", name: item.author },
              publisher: { "@id": `${SITE_URL}/#organization` },
              mainEntityOfPage: `${SITE_URL}/meme/${item.id}`,
              articleSection: item.categoryName,
              keywords: item.tags.join(","),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: item.categoryName, item: `${SITE_URL}/category/${item.category}` },
                { "@type": "ListItem", position: 3, name: item.title, item: `${SITE_URL}/meme/${item.id}` },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!item) {
    return (
      <section className="mx-auto w-full px-4 py-24 text-center md:px-8">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-3xl text-foreground-950">这条内容找不到了</h1>
        <p className="mt-3 text-sm text-foreground-600">它可能已经被移动或下架，去首页看看最新的吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </section>
    );
  }

  const related = memeItems
    .filter((meme) => meme.category === item.category && meme.id !== item.id)
    .slice(0, 3);

  const shareUrl = `${SITE_URL}/meme/${item.id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb
            items={[
              { label: item.categoryName, to: `/category/${item.category}` },
              { label: item.title },
            ]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${item.category}`}
              className="rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50"
            >
              {item.categoryName}
            </Link>
            {item.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                #{tag}
              </span>
            ))}
          </div>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl leading-snug text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-[1fr_336px] lg:gap-12">
          <div className="min-w-0">
            <figure className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <div className="w-full overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
                <img
                  src={item.cover}
                  alt={`${item.title} - 131吃瓜${item.categoryName}素材`}
                  title={`${item.title} · 131吃瓜`}
                  className="h-full w-full object-cover"
                />
              </div>
              <figcaption className="flex flex-col gap-4 border-t border-background-200 bg-background-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-primary-500"></i>热度 {item.hot}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-download-2-line text-accent-600"></i>
                    {item.downloads} 次保存
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {item.date}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={item.cover}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-download-2-line text-sm"></i>
                    </span>
                    打开原图保存
                  </a>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className={copied ? "ri-check-line text-sm" : "ri-link text-sm"}></i>
                    </span>
                    {copied ? "链接已复制" : "复制分享链接"}
                  </button>
                </div>
              </figcaption>
            </figure>

            <div className="mt-10">
              <h2 className="font-heading text-2xl text-foreground-950">这张图为什么好用</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{item.summary}</p>
              <ul className="mt-6 space-y-3">
                {item.points.map((point) => (
                  <li key={point} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-check-line text-base"></i>
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-800">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-12 border-t border-background-200 pt-10">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl text-foreground-950">神评论</h2>
                <span className="text-xs text-foreground-500">共 {item.comments.length} 条</span>
              </div>
              <ul className="mt-6 space-y-4">
                {item.comments.map((comment) => (
                  <li key={comment.user} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-base text-secondary-900">
                      {comment.user.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground-900">{comment.text}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-foreground-500">
                        <span>{comment.user}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-thumb-up-line text-accent-600"></i>
                          {comment.likes}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="min-w-0 space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-lg text-foreground-950">素材信息</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">分类</dt>
                  <dd className="text-foreground-900">{item.categoryName}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">来源</dt>
                  <dd className="text-foreground-900">{item.author}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">收录日期</dt>
                  <dd className="text-foreground-900">{item.date}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">热度</dt>
                  <dd className="text-foreground-900">{item.hot}</dd>
                </div>
              </dl>
              <p className="mt-4 rounded-md bg-background-50 p-3 text-xs leading-relaxed text-foreground-600">
                站内素材仅供日常聊天与交流使用，用于商业场景前请先通过联系页面取得授权。
              </p>
            </div>

            {related.length > 0 && (
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-lg text-foreground-950">同类推荐</h2>
                <ul className="mt-4 space-y-4">
                  {related.map((meme) => (
                    <li key={meme.id}>
                      <Link to={`/meme/${meme.id}`} className="group flex items-center gap-3">
                        <div className="h-14 w-16 shrink-0 overflow-hidden rounded-md bg-background-50">
                          <img
                            src={meme.cover}
                            alt={`${meme.title} 缩略图`}
                            title={`${meme.title} · 131吃瓜`}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <span className="min-w-0">
                          <span className="block truncate font-heading text-sm text-foreground-950 group-hover:text-primary-700">
                            {meme.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-500">热度 {meme.hot}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/category/${item.category}`}
                  className="mt-5 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  更多{item.categoryName}
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </Link>
              </div>
            )}
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full bg-background-100">
          <div className="mx-auto w-full px-4 py-14 md:px-8 md:py-16">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">你可能还想存这些</h2>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((meme) => (
                <MemeCard key={meme.id} item={meme} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}